import { FC, ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import numeral from 'numeral';
import { DailyNavClosedTransactionAverage, DailyNavClosedTransactionGroup } from 'api';
import { formatProviderCode, shortDate } from 'utilities';
import { ClosedTransactionsDialogProps } from './ClosedTransactionsDialog.props';

const groupCaption = (group: DailyNavClosedTransactionGroup): string => {
  const providers = group.provider_codes.map(formatProviderCode).join(', ');
  const tradeLabel = group.transaction_count === 1 ? '1 trade' : `${group.transaction_count} trades`;
  return [providers, tradeLabel].filter(Boolean).join(' · ');
};

// Same two decimals as every price above it, so the summary and the trades it
// summarizes cannot read as different precisions.
const formatPrice = (value: string): string => `$${numeral(Number(value)).format('0,0.00')}`;

const formatAveragePrice = (value: string | null): string => (value === null ? '—' : formatPrice(value));

// A long window of heavy trading would otherwise push the tooltip off screen.
const TOOLTIP_TRADE_LIMIT = 12;

// Names the exact prints behind the figure. Not inferable from the table above:
// that list spans two years and stops at ten, while the average spans three
// months and has no cap, so neither one contains the other.
const averageTooltip = (average: DailyNavClosedTransactionAverage): ReactNode => {
  if (average.contributions.length === 0) {
    return 'No closed trades in the trailing 3 months.';
  }
  const shown = average.contributions.slice(0, TOOLTIP_TRADE_LIMIT);
  const hidden = average.contributions.length - shown.length;
  return (
    <Box data-testid="closed-transactions-average-tooltip">
      <Typography variant="caption" fontWeight={700} display="block">
        Averaged from {average.transaction_count === 1 ? '1 trade' : `${average.transaction_count} trades`} ·{' '}
        {shortDate(average.start_date)} – {shortDate(average.end_date)}
      </Typography>
      {shown.map(contribution => (
        <Typography
          key={`${contribution.transaction_date}-${contribution.price_per_share}-${contribution.security_type ?? ''}`}
          variant="caption"
          display="block">
          {shortDate(contribution.transaction_date)} — {formatPrice(contribution.price_per_share)}
          {contribution.security_type ? ` · ${contribution.security_type}` : ''} ·{' '}
          {contribution.provider_codes.map(formatProviderCode).join(', ')}
        </Typography>
      ))}
      {hidden > 0 && (
        <Typography variant="caption" display="block" fontStyle="italic">
          + {hidden} more
        </Typography>
      )}
      {average.median_price !== null && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          Median {formatPrice(average.median_price)}
        </Typography>
      )}
    </Box>
  );
};

// States the basis, because the number is deliberately not the obvious one: it
// weights every trade equally and blends share classes.
const averageBasis = (average: DailyNavClosedTransactionAverage): string => {
  const window = `${shortDate(average.start_date)} – ${shortDate(average.end_date)}`;
  if (average.transaction_count === 0) {
    return `No closed trades in the trailing 3 months (${window}).`;
  }
  const trades = average.transaction_count === 1 ? '1 trade' : `${average.transaction_count} trades`;
  const classes = average.security_types.length ? average.security_types.join(', ') : 'unclassified';
  return `Simple average of ${trades} · ${window} · all share classes (${classes})`;
};

const ClosedTransactionsDialog: FC<ClosedTransactionsDialogProps> = ({ row, open, onClose }) => {
  const groups = row?.record.secondary_breakdown?.closed_transactions ?? [];
  const average = row?.record.secondary_breakdown?.closed_transaction_average ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      id="closed-transactions-dialog"
      data-testid="closed-transactions-dialog">
      <DialogTitle>
        <Typography variant="h6">
          Closed Transactions{row?.companyName ? ` — ${row.companyName}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Up to the 10 most recent groups. Trades after the last valuation are highlighted.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {groups.length === 0 && (
          <Alert severity="info">No closed secondary transactions are available for this row.</Alert>
        )}
        {groups.length > 0 && (
          <Table size="small" data-testid="closed-transactions-table">
            <TableHead>
              <TableRow>
                <TableCell>Transaction</TableCell>
                <TableCell>Share Class</TableCell>
                <TableCell>Sources</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group, index) => {
                const highlightSx = {
                  color: group.since_last_valuation ? 'error.main' : 'inherit',
                  fontWeight: group.since_last_valuation ? 700 : 400,
                };
                return (
                  <TableRow
                    key={`${group.transaction_date}-${group.price_per_share}-${group.security_type ?? ''}-${index}`}
                    data-since-last-valuation={group.since_last_valuation ? 'true' : undefined}>
                    <TableCell>
                      <Typography variant="body2" sx={highlightSx}>
                        {group.display}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        data-testid={`closed-transactions-share-class-${index}`}
                        sx={highlightSx}>
                        {group.security_type || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {groupCaption(group)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {/* Summarizes the list, so it needs a list -- the empty Alert already
            says there is nothing to average. */}
        {groups.length > 0 && average && (
          <Box
            mt={2}
            pt={1.5}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            data-testid="closed-transactions-average">
            <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={2}>
              <Typography variant="body2" fontWeight={700}>
                3-month average price
              </Typography>
              <Tooltip arrow placement="left" title={averageTooltip(average)}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  data-testid="closed-transactions-average-price"
                  sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                  {formatAveragePrice(average.average_price)}
                </Typography>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
              data-testid="closed-transactions-average-basis">
              {averageBasis(average)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} id="closed-transactions-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosedTransactionsDialog;
